<template>
  <div class="top-partners">
    <AgGridVue
      class="w-100"
      dom-layout="autoHeight"
      :column-defs="columnDefs"
      :row-data="topPartners"
      :grid-options="gridOptions"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<script>
import { pick } from 'lodash';
import { mapActions, mapState } from 'pinia';

// composables
import { useInternationalization } from '@/composables/use-internationalization';
import { useFormatNumber } from '@/composables/use-format-number';

// stores
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';

export default {
  setup() {
    const { tkey } = useInternationalization('assortmentCanvasPage.productDashboard');
    const { formatNumber } = useFormatNumber();

    return { tkey, formatNumber };
  },

  data() {
    return {
      gridApi: null,
      gridOptions: {
        suppressContextMenu: true,
        suppressCellFocus: true,
        rowHeight: 20,
        defaultColDef: {
          filter: false,
          sortable: false,
          resizable: false,
          editable: false,
          suppressMovable: true,
          suppressHeaderMenuButton: true,
          menuTabs: [],
          wrapHeaderText: true,
          autoHeaderHeight: true,
          cellStyle: { paddingLeft: '4px', paddingRight: '4px' },
        },
        statusBar: false,
      },
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'getIndexedCanvasProducts',
      'getSelectedProductTopPartners',
      'getDashboardProduct',
    ]),

    columnDefs() {
      return [
        {
          headerName: this.tkey('productKey'),
          field: 'productKeyDisplay',
          flex: 2,
          tooltipField: 'productKeyDisplay',
        },
        {
          headerName: this.tkey('description'),
          field: 'itemDescription',
          flex: 6,
          tooltipField: 'itemDescription',
        },
        {
          headerName: this.tkey('switchingProbability'),
          field: 'switchingProbabilityDisplay',
          width: 80,
          tooltipField: 'switchingProbabilityDisplay',
        },
      ];
    },

    topPartners() {
      if (!this.getSelectedProductTopPartners.length) return [];
      return this.getSelectedProductTopPartners.map(tp => {
        const formattedValue = this.formatNumber({ number: tp.value * 100, format: 'float' });
        const tooSmall = formattedValue === '0';
        const percentage = `${formattedValue}%`;
        const switchingProbabilityDisplay = tooSmall
          ? `${percentage} ${this.tkey('valueTooSmall')}`
          : percentage;

        return {
          ...tp,
          ...pick(this.getIndexedCanvasProducts[tp.to], [
            'itemDescription',
            'productKey',
            'productKeyDisplay',
          ]),
          switchingProbabilityDisplay,
        };
      });
    },
  },

  watch: {
    getDashboardProduct: {
      immediate: true,
      async handler(newValue) {
        if (!newValue) return;
        await this.fetchProductTopPartners({
          productKey: newValue.productKey,
          cannGroupId: newValue.cannGroupId,
        });
      },
    },
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, ['fetchProductTopPartners']),

    onGridReady(params) {
      this.gridApi = params.api;
    },
  },
};
</script>

<style lang="scss" scoped>
.top-partners {
  width: 100%;
}

:deep(.ag-header-cell) {
  padding-left: 4px;
  padding-right: 4px;
}
</style>
